import { USER_DESTINATIONS, type UserDestinationSlot } from '@utazzo/shared';
import { UzButton, UzCombobox } from '@utazzo/ui';
import { useEffect, useMemo, useState } from 'react';

import { useDestinationPickerSearch } from '@/features/leads/use-lead-options';
import { destinationPriorityLabel } from '@/features/users/user-display';

const NONE = '__none__';

/** Per-destination numbers (marketing, LPS); a destination without one has null. */
type CapacityById = ReadonlyMap<string, number>;
interface Capacities {
  marketing: CapacityById;
  lps: CapacityById;
}

function rebuildDestinations(
  primaryId: string | undefined,
  additionalIds: string[],
  capacities: Capacities,
): UserDestinationSlot[] {
  const slot = (destinationId: string, priority: number): UserDestinationSlot => ({
    destinationId,
    priority,
    marketingLeadCapacity: capacities.marketing.get(destinationId) ?? null,
    lpsLeadCapacity: capacities.lps.get(destinationId) ?? null,
  });
  const rows: UserDestinationSlot[] = [];
  if (primaryId) rows.push(slot(primaryId, 1));
  additionalIds.forEach((destinationId, index) => {
    rows.push(slot(destinationId, index + 2));
  });
  return rows;
}

/** A small whole-number box beside a destination. Empty = not set. */
const CapacityInput = ({
  value,
  onChange,
  disabled,
  placeholder,
  label,
  title,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean;
  placeholder: string;
  label: string;
  title: string;
}): React.JSX.Element => (
  <input
    type="number"
    inputMode="numeric"
    min={1}
    max={USER_DESTINATIONS.MAX_DAILY_LEAD_CAPACITY}
    step={1}
    value={value ?? ''}
    placeholder={placeholder}
    aria-label={label}
    title={title}
    disabled={disabled}
    onChange={(e) => {
      const n = Math.floor(Number(e.target.value));
      onChange(
        e.target.value.trim() === '' || !Number.isFinite(n) || n < 1
          ? null
          : Math.min(n, USER_DESTINATIONS.MAX_DAILY_LEAD_CAPACITY),
      );
    }}
    className="h-10 w-28 shrink-0 rounded-md border border-zinc-300 bg-white px-2.5 text-sm tabular-nums placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none disabled:bg-zinc-50 disabled:text-zinc-400"
  />
);

/** Marketing + LPS, side by side after the destination picker. */
const CapacityInputs = ({
  marketing,
  lps,
  onMarketingChange,
  onLpsChange,
  disabled,
}: {
  marketing: number | null;
  lps: number | null;
  onMarketingChange: (next: number | null) => void;
  onLpsChange: (next: number | null) => void;
  disabled?: boolean;
}): React.JSX.Element => (
  <>
    <CapacityInput
      value={marketing}
      onChange={onMarketingChange}
      disabled={disabled}
      placeholder="Marketing / day"
      label="Marketing leads per day"
      title="Marketing leads per day for this destination. Blank = not set."
    />
    <CapacityInput
      value={lps}
      onChange={onLpsChange}
      disabled={disabled}
      placeholder="LPS / day"
      label="LPS leads per day"
      title="LPS leads auto-assigned to this user for this destination per day (counted on their own). Blank = this user gets no LPS leads for this destination."
    />
  </>
);

function splitDestinations(value: UserDestinationSlot[]): {
  primaryId: string | undefined;
  additionalIds: string[];
} {
  const sorted = [...value].sort((a, b) => a.priority - b.priority);
  const primary = sorted.find((d) => d.priority === 1);
  const additional = sorted.filter((d) => d.priority > 1).map((d) => d.destinationId);
  return { primaryId: primary?.destinationId, additionalIds: additional };
}

function withSavedOption(
  options: Array<{ value: string; label: string }>,
  selectedId: string | undefined,
  savedLabels: Record<string, string>,
): Array<{ value: string; label: string }> {
  if (!selectedId || options.some((o) => o.value === selectedId)) return options;
  const label = savedLabels[selectedId] ?? selectedId;
  return [{ value: selectedId, label }, ...options];
}

interface UserDestinationsEditorProps {
  value: UserDestinationSlot[];
  onChange: (next: UserDestinationSlot[]) => void;
  savedLabels?: Record<string, string>;
  disabled?: boolean;
  enabled?: boolean;
  error?: string;
  /** Ops users carry no per-day caps: the two capacity boxes are not shown. */
  hideCapacity?: boolean;
}

export const UserDestinationsEditor = ({
  value,
  onChange,
  savedLabels = {},
  disabled,
  enabled = true,
  error,
  hideCapacity = false,
}: UserDestinationsEditorProps): React.JSX.Element => {
  const { primaryId, additionalIds } = useMemo(() => splitDestinations(value), [value]);
  const capacities = useMemo<Capacities>(
    () => ({
      marketing: new Map(
        value.flatMap((d) =>
          d.marketingLeadCapacity != null
            ? [[d.destinationId, d.marketingLeadCapacity] as const]
            : [],
        ),
      ),
      lps: new Map(
        value.flatMap((d) =>
          d.lpsLeadCapacity != null ? [[d.destinationId, d.lpsLeadCapacity] as const] : [],
        ),
      ),
    }),
    [value],
  );
  const setCapacity = (
    kind: keyof Capacities,
    destinationId: string,
    capacity: number | null,
  ): void => {
    const next = new Map(capacities[kind]);
    if (capacity === null) next.delete(destinationId);
    else next.set(destinationId, capacity);
    onChange(rebuildDestinations(primaryId, additionalIds, { ...capacities, [kind]: next }));
  };
  const [primarySearch, setPrimarySearch] = useState('');
  const [additionalSearchByIndex, setAdditionalSearchByIndex] = useState<Record<string, string>>(
    {},
  );
  const [pendingAdditionalRows, setPendingAdditionalRows] = useState(0);

  useEffect(() => {
    if (!enabled) setPendingAdditionalRows(0);
  }, [enabled]);

  const primaryQuery = useDestinationPickerSearch({ search: primarySearch, enabled });
  const pickedIds = useMemo(
    () => new Set([primaryId, ...additionalIds].filter(Boolean) as string[]),
    [primaryId, additionalIds],
  );

  const primaryOptions = useMemo(() => {
    const filtered = primaryQuery.options.filter(
      (o) => !pickedIds.has(o.value) || o.value === primaryId,
    );
    const opts = withSavedOption(filtered, primaryId, savedLabels);
    return [{ value: NONE, label: 'Select destination' }, ...opts];
  }, [primaryQuery.options, pickedIds, primaryId, savedLabels]);

  const setPrimary = (destinationId: string | undefined): void => {
    onChange(rebuildDestinations(destinationId, additionalIds, capacities));
  };

  const setAdditionalAt = (index: number, destinationId: string): void => {
    const next = [...additionalIds];
    next[index] = destinationId;
    onChange(rebuildDestinations(primaryId, next, capacities));
  };

  const addAdditional = (): void => {
    const total = additionalIds.length + pendingAdditionalRows + (primaryId ? 1 : 0);
    if (total >= USER_DESTINATIONS.MAX_COUNT) return;
    setPendingAdditionalRows((n) => n + 1);
  };

  const removeAdditional = (index: number): void => {
    const next = additionalIds.filter((_, i) => i !== index);
    onChange(rebuildDestinations(primaryId, next, capacities));
  };

  const removePendingRow = (index: number): void => {
    setPendingAdditionalRows((n) => Math.max(0, n - 1));
    setAdditionalSearchByIndex((prev) => {
      const next = { ...prev };
      delete next[`pending-${index}`];
      return next;
    });
  };

  const commitPendingRow = (destinationId: string): void => {
    onChange(rebuildDestinations(primaryId, [...additionalIds, destinationId], capacities));
    setPendingAdditionalRows((n) => Math.max(0, n - 1));
  };

  const totalSelected = (primaryId ? 1 : 0) + additionalIds.length;
  const canAddMore = totalSelected + pendingAdditionalRows < USER_DESTINATIONS.MAX_COUNT;

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <div className="w-8 shrink-0 pt-2.5 text-xs font-medium text-zinc-700">
          {destinationPriorityLabel(1)}
        </div>
        <div className="min-w-0 flex-1">
          <UzCombobox<string>
            name="user-destination-primary"
            placeholder={primaryQuery.isLoading ? 'Loading…' : 'Search destination'}
            searchPlaceholder="Type to search destinations…"
            options={primaryOptions}
            value={primaryId ?? NONE}
            onValueChange={(v) => setPrimary(v === NONE ? undefined : v)}
            onSearchChange={setPrimarySearch}
            loading={primaryQuery.isLoading || primaryQuery.isFetching}
            emptyText={primarySearch.trim().length >= 2 ? 'No matches' : 'Type to search'}
            disabled={disabled}
            error={error}
          />
          {primaryQuery.isError && (
            <p className="mt-1 text-xs text-rose-600">Failed to load destinations. Try again.</p>
          )}
        </div>
        {hideCapacity ? null : (
          <CapacityInputs
            marketing={primaryId ? (capacities.marketing.get(primaryId) ?? null) : null}
            lps={primaryId ? (capacities.lps.get(primaryId) ?? null) : null}
            onMarketingChange={(n) => primaryId && setCapacity('marketing', primaryId, n)}
            onLpsChange={(n) => primaryId && setCapacity('lps', primaryId, n)}
            disabled={disabled || !primaryId}
          />
        )}
        {/* P1 is mandatory - no Remove. Spacer keeps columns aligned with P2/P3. */}
        <div className="w-18 shrink-0" aria-hidden />
      </div>

      {additionalIds.length > 0 && (
        <div className="space-y-2">
          {additionalIds.map((id, index) => (
            <AdditionalDestinationRow
              key={`additional-${id}-${index}`}
              priority={index + 2}
              index={index}
              value={id}
              search={additionalSearchByIndex[index] ?? ''}
              onSearchChange={(next) =>
                setAdditionalSearchByIndex((prev) => ({ ...prev, [index]: next }))
              }
              pickedIds={pickedIds}
              savedLabels={savedLabels}
              enabled={enabled}
              disabled={disabled}
              hideCapacity={hideCapacity}
              onChange={(destinationId) => setAdditionalAt(index, destinationId)}
              marketing={capacities.marketing.get(id) ?? null}
              onMarketingChange={(n) => setCapacity('marketing', id, n)}
              lps={capacities.lps.get(id) ?? null}
              onLpsChange={(n) => setCapacity('lps', id, n)}
              onRemove={() => removeAdditional(index)}
            />
          ))}
        </div>
      )}

      {Array.from({ length: pendingAdditionalRows }).map((_, index) => (
        <PendingDestinationRow
          key={`pending-${index}`}
          priority={2 + additionalIds.length + index}
          index={index}
          search={additionalSearchByIndex[`pending-${index}`] ?? ''}
          onSearchChange={(next) =>
            setAdditionalSearchByIndex((prev) => ({ ...prev, [`pending-${index}`]: next }))
          }
          pickedIds={pickedIds}
          enabled={enabled}
          disabled={disabled}
          onCommit={commitPendingRow}
          onRemove={() => removePendingRow(index)}
        />
      ))}

      <UzButton
        type="button"
        variant="outline"
        size="sm"
        onClick={addAdditional}
        disabled={disabled || !canAddMore}
      >
        Add destination
      </UzButton>
    </div>
  );
};

interface AdditionalDestinationRowProps {
  priority: number;
  index: number;
  value: string;
  search: string;
  onSearchChange: (next: string) => void;
  pickedIds: Set<string>;
  savedLabels: Record<string, string>;
  enabled: boolean;
  disabled?: boolean;
  hideCapacity?: boolean;
  onChange: (destinationId: string) => void;
  marketing: number | null;
  onMarketingChange: (next: number | null) => void;
  lps: number | null;
  onLpsChange: (next: number | null) => void;
  onRemove: () => void;
}

const AdditionalDestinationRow = ({
  priority,
  index,
  value,
  search,
  onSearchChange,
  pickedIds,
  savedLabels,
  enabled,
  disabled,
  onChange,
  marketing,
  onMarketingChange,
  lps,
  onLpsChange,
  onRemove,
  hideCapacity = false,
}: AdditionalDestinationRowProps): React.JSX.Element => {
  const query = useDestinationPickerSearch({ search, enabled });
  const options = useMemo(() => {
    const filtered = query.options.filter((o) => !pickedIds.has(o.value) || o.value === value);
    const opts = withSavedOption(filtered, value, savedLabels);
    return [{ value: NONE, label: 'Select destination' }, ...opts];
  }, [query.options, pickedIds, value, savedLabels]);

  return (
    <div className="flex items-start gap-2">
      <div className="w-8 shrink-0 pt-2.5 text-xs font-medium text-zinc-700">
        {destinationPriorityLabel(priority)}
      </div>
      <div className="min-w-0 flex-1">
        <UzCombobox<string>
          name={`user-destination-additional-${index}`}
          placeholder={query.isLoading ? 'Loading…' : 'Search destination'}
          searchPlaceholder="Type to search destinations…"
          options={options}
          value={value}
          onValueChange={(v) => {
            if (v !== NONE) onChange(v);
          }}
          onSearchChange={onSearchChange}
          loading={query.isLoading || query.isFetching}
          emptyText={search.trim().length >= 2 ? 'No matches' : 'Type to search'}
          disabled={disabled}
        />
      </div>
      {hideCapacity ? null : (
        <CapacityInputs
          marketing={marketing}
          lps={lps}
          onMarketingChange={onMarketingChange}
          onLpsChange={onLpsChange}
          disabled={disabled}
        />
      )}
      <UzButton
        type="button"
        variant="ghost"
        size="sm"
        className="mt-1 shrink-0 text-zinc-500"
        onClick={onRemove}
        disabled={disabled}
        aria-label="Remove destination"
      >
        Remove
      </UzButton>
    </div>
  );
};

interface PendingDestinationRowProps {
  priority: number;
  index: number;
  search: string;
  onSearchChange: (next: string) => void;
  pickedIds: Set<string>;
  enabled: boolean;
  disabled?: boolean;
  onCommit: (destinationId: string) => void;
  onRemove: () => void;
}

const PendingDestinationRow = ({
  priority,
  index,
  search,
  onSearchChange,
  pickedIds,
  enabled,
  disabled,
  onCommit,
  onRemove,
}: PendingDestinationRowProps): React.JSX.Element => {
  const query = useDestinationPickerSearch({ search, enabled });
  const options = useMemo(() => {
    const filtered = query.options.filter((o) => !pickedIds.has(o.value));
    return [{ value: NONE, label: 'Select destination' }, ...filtered];
  }, [query.options, pickedIds]);

  return (
    <div className="flex items-start gap-2">
      <div className="w-8 shrink-0 pt-2.5 text-xs font-medium text-zinc-700">
        {destinationPriorityLabel(priority)}
      </div>
      <div className="min-w-0 flex-1">
        <UzCombobox<string>
          name={`user-destination-pending-${index}`}
          placeholder={query.isLoading ? 'Loading…' : 'Search destination'}
          searchPlaceholder="Type to search destinations…"
          options={options}
          value={NONE}
          onValueChange={(v) => {
            if (v !== NONE) onCommit(v);
          }}
          onSearchChange={onSearchChange}
          loading={query.isLoading || query.isFetching}
          emptyText={search.trim().length >= 2 ? 'No matches' : 'Type to search'}
          disabled={disabled}
        />
      </div>
      <UzButton
        type="button"
        variant="ghost"
        size="sm"
        className="mt-1 shrink-0 text-zinc-500"
        onClick={onRemove}
        disabled={disabled}
        aria-label="Cancel new destination"
      >
        Remove
      </UzButton>
    </div>
  );
};
